import { useEffect, useState } from "react";
import { Activity, TrendingUp, Trophy, MonitorPlay, AlertTriangle, RotateCw } from "lucide-react";
import { COLORS } from "../styles/tokens";
import { useCountUp } from "../hooks";
import { getCoverageByDma, getCoverageSummary, ApiError } from "../lib/api";
import type { CoverageSummaryResponse, DmaCoverageRow } from "../types/realsynq";

interface CoverageViewProps {
  isMobile: boolean;
}

const WINDOWS: Array<{ days: number; label: string }> = [
  { days: 1, label: "24H" },
  { days: 7, label: "7D" },
  { days: 30, label: "30D" },
];

export function CoverageView({ isMobile }: CoverageViewProps) {
  const [lookbackDays, setLookbackDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CoverageSummaryResponse | null>(null);
  const [dmaRows, setDmaRows] = useState<DmaCoverageRow[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([getCoverageSummary(lookbackDays), getCoverageByDma(lookbackDays, 20)])
      .then(([s, d]) => {
        if (cancelled) return;
        setSummary(s);
        setDmaRows(d.rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? err.detail || `Request failed (${err.status})`
            : "Couldn't reach the coverage service.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lookbackDays, reloadKey]);

  const m = summary?.metrics;
  const hasData =
    !!summary &&
    summary.source_available &&
    !!m &&
    (m.bid_requests + m.opportunities + m.wins + m.impressions) > 0;

  return (
    <div style={{ padding: isMobile ? "40px 16px 80px" : "60px 32px", maxWidth: 1200, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.15em", marginBottom: 12 }}>
        ◆ LIMELIGHT BID COVERAGE
      </div>
      <h2
        className="display"
        style={{
          fontSize: isMobile ? "clamp(32px, 9vw, 40px)" : 44,
          fontWeight: 700,
          margin: "0 0 16px",
          lineHeight: 1,
        }}
      >
        Bid coverage, end to end.
      </h2>
      <p
        style={{
          color: COLORS.inkDim,
          fontSize: isMobile ? 14 : 16,
          maxWidth: 720,
          lineHeight: 1.6,
          marginBottom: isMobile ? 28 : 40,
        }}
      >
        Of all bid requests Limelight receives, how many land while RealSynq signals are
        active. Sourced from the hourly Direction&nbsp;B pull (<span className="mono" style={{ fontSize: 13 }}>realsynq_bid_daily</span>),
        aggregated across DMA, device, and channel. Per-signal attribution arrives with
        Direction&nbsp;A deal-ID enrichment.
      </p>

      {/* Window selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        <span className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.12em", marginRight: 4 }}>
          WINDOW
        </span>
        {WINDOWS.map(({ days, label }) => {
          const sel = lookbackDays === days;
          return (
            <button
              key={days}
              onClick={() => setLookbackDays(days)}
              className="mono"
              style={{
                fontSize: 11,
                padding: "5px 14px",
                borderRadius: 5,
                border: `1px solid ${sel ? COLORS.cyan : COLORS.border}`,
                background: sel ? `${COLORS.cyan}18` : "transparent",
                color: sel ? COLORS.cyan : COLORS.inkMute,
                letterSpacing: "0.06em",
                cursor: "pointer",
                transition: "all 0.12s",
              }}
            >
              {label}
            </button>
          );
        })}
        {summary?.last_updated && hasData && (
          <span className="mono" style={{ fontSize: 10, color: COLORS.inkMute, marginLeft: "auto" }}>
            updated {new Date(summary.last_updated).toLocaleString()}
          </span>
        )}
      </div>

      {loading && <CoverageSkeleton isMobile={isMobile} />}

      {!loading && error && (
        <StatePanel
          icon={<AlertTriangle size={22} color={COLORS.rose} />}
          accent={COLORS.rose}
          title="Coverage service unavailable"
          body={error}
          action={
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="mono"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 16,
                padding: "8px 16px",
                borderRadius: 6,
                border: `1px solid ${COLORS.rose}55`,
                background: `${COLORS.rose}12`,
                color: COLORS.rose,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <RotateCw size={13} /> Retry
            </button>
          }
        />
      )}

      {!loading && !error && !hasData && (
        <StatePanel
          icon={<Activity size={22} color={COLORS.inkMute} />}
          accent={COLORS.border}
          dashed
          title="Coverage pipeline not yet live"
          body={
            summary && !summary.source_available
              ? "The warehouse read path isn't configured in this environment. Coverage data appears once the Pull 8 ingestion is deployed and MySQL connectivity is set."
              : "No bid-request activity recorded for this window yet. Data appears once the Pull 8 ingestion starts populating realsynq_bid_daily."
          }
        />
      )}

      {!loading && !error && hasData && m && (
        <>
          {/* Metric cards */}
          <div
            key={`metrics-${lookbackDays}-${reloadKey}`}
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)",
              gap: isMobile ? 10 : 14,
              marginBottom: isMobile ? 24 : 32,
            }}
          >
            <MetricCard label="Bid Requests" value={m.bid_requests} accent={COLORS.cyan} Icon={Activity} />
            <MetricCard label="Opportunities" value={m.opportunities} accent={COLORS.blue} Icon={TrendingUp} />
            <MetricCard label="Wins" value={m.wins} accent={COLORS.lime} Icon={Trophy} />
            <MetricCard label="Impressions" value={m.impressions} accent={COLORS.amber} Icon={MonitorPlay} />
            <WinRateCard winRate={summary.win_rate} />
          </div>

          {/* Top-DMA table */}
          <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.14em", marginBottom: 12 }}>
            ◆ TOP DMAs BY BID REQUESTS
          </div>
          <div
            style={{
              background: COLORS.bgCard,
              backdropFilter: "blur(10px)",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <DmaTable rows={dmaRows} isMobile={isMobile} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Metric cards ─────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  accent,
  Icon,
}: {
  label: string;
  value: number;
  accent: string;
  Icon: typeof Activity;
}) {
  const animated = useCountUp(value);
  return (
    <div
      style={{
        background: COLORS.bgCard,
        backdropFilter: "blur(10px)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: 18,
      }}
    >
      <Icon size={15} color={accent} style={{ marginBottom: 10 }} />
      <div className="mono display" style={{ fontSize: 26, fontWeight: 700, color: COLORS.ink, lineHeight: 1.1 }}>
        {animated.toLocaleString()}
      </div>
      <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.1em", marginTop: 6 }}>
        {label.toUpperCase()}
      </div>
    </div>
  );
}

function WinRateCard({ winRate }: { winRate: number | null }) {
  const pct = winRate == null ? null : (winRate * 100).toFixed(1);
  return (
    <div
      style={{
        background: COLORS.bgCard,
        backdropFilter: "blur(10px)",
        border: `1px solid ${COLORS.violet}33`,
        borderRadius: 10,
        padding: 18,
      }}
    >
      <Trophy size={15} color={COLORS.violet} style={{ marginBottom: 10 }} />
      <div className="mono display" style={{ fontSize: 26, fontWeight: 700, color: COLORS.ink, lineHeight: 1.1 }}>
        {pct == null ? "—" : `${pct}%`}
      </div>
      <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.1em", marginTop: 6 }}>
        WIN RATE
      </div>
    </div>
  );
}

// ─── DMA table ────────────────────────────────────────────────────────────

function DmaTable({ rows, isMobile }: { rows: DmaCoverageRow[]; isMobile: boolean }) {
  const numCell: React.CSSProperties = {
    textAlign: "right",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: isMobile ? 11 : 12,
    color: COLORS.ink,
    padding: isMobile ? "10px 12px" : "11px 16px",
    whiteSpace: "nowrap",
  };
  const headCell: React.CSSProperties = {
    textAlign: "right",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 9,
    letterSpacing: "0.1em",
    color: COLORS.inkMute,
    padding: isMobile ? "10px 12px" : "11px 16px",
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
  };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ ...headCell, textAlign: "left" }}>DMA</th>
          <th style={headCell}>BID REQ</th>
          <th style={headCell}>OPPS</th>
          <th style={headCell}>WINS</th>
          {!isMobile && <th style={headCell}>IMPRESSIONS</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.metro_code} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
            <td
              className="mono"
              style={{
                textAlign: "left",
                fontSize: isMobile ? 11 : 12,
                color: COLORS.cyan,
                padding: isMobile ? "10px 12px" : "11px 16px",
              }}
            >
              {r.metro_code}
            </td>
            <td style={numCell}>{r.bid_requests.toLocaleString()}</td>
            <td style={numCell}>{r.opportunities.toLocaleString()}</td>
            <td style={numCell}>{r.wins.toLocaleString()}</td>
            {!isMobile && <td style={numCell}>{r.impressions.toLocaleString()}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Loading + state panels ───────────────────────────────────────────────

function CoverageSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)",
          gap: isMobile ? 10 : 14,
          marginBottom: isMobile ? 24 : 32,
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="skeleton"
            style={{
              height: 96,
              background: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
            }}
          />
        ))}
      </div>
      <div
        className="skeleton"
        style={{ height: 220, background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 10 }}
      />
    </div>
  );
}

function StatePanel({
  icon,
  accent,
  title,
  body,
  action,
  dashed,
}: {
  icon: React.ReactNode;
  accent: string;
  title: string;
  body: string;
  action?: React.ReactNode;
  dashed?: boolean;
}) {
  return (
    <div
      style={{
        padding: "48px 32px",
        textAlign: "center",
        border: `1px ${dashed ? "dashed" : "solid"} ${accent}`,
        borderRadius: 10,
        background: COLORS.bgCard,
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>{icon}</div>
      <div className="display" style={{ fontSize: 18, fontWeight: 600, color: COLORS.ink, marginBottom: 8 }}>
        {title}
      </div>
      <p style={{ fontSize: 14, color: COLORS.inkDim, margin: "0 auto", maxWidth: 460, lineHeight: 1.6 }}>{body}</p>
      {action}
    </div>
  );
}
